import { useEffect, useState } from "react";
import { createLogger } from "@/logging";
import { isAbortError } from "@/utils";
import type { EpisodeDiagnosticIndex } from "./episodeDiagnosticValues";
import { loadEpisodeDiagnostics, type EpisodeDiagnosticsProgress } from "./loadEpisodeDiagnostics";

const logger = createLogger("components/dataset/diagnostics");

/**
 * 데이터셋 큐레이션 지표를 참고용 오버레이로 1회 적재한다.
 *
 * 밴드(`useCollectionDiagnostics`)와 같은 자리, 같은 게이트에서 호출하고 결과를 자식에 전달한다.
 * 컬렉션 단위로 한 번만 적재해서, 에피소드를 바꿔도 다시 받지 않기 위해서다.
 * 조회가 실패하면 밴드와 같이 숨긴다. 미리보기 자체는 계속 동작한다.
 */

export type EpisodeDiagnosticsState =
  | { status: "loading"; progress: EpisodeDiagnosticsProgress | null }
  | { status: "ready"; index: EpisodeDiagnosticIndex }
  // 조회 실패 또는 미산출. 소비부는 블록을 그리지 않는다.
  | { status: "unavailable" };

export function useEpisodeDiagnostics(
  datasetUuid: string,
  enabled: boolean,
): EpisodeDiagnosticsState | null {
  const [state, setState] = useState<EpisodeDiagnosticsState | null>(null);

  useEffect(() => {
    if (!enabled || !datasetUuid) {
      setState(null);
      return;
    }
    const controller = new AbortController();
    // 데이터셋을 바꾸면 이전 색인을 즉시 버린다. 새 적재가 도착하기 전에 stale 값을 표시하지 않는다.
    setState({ status: "loading", progress: null });
    void loadEpisodeDiagnostics(datasetUuid, {
      signal: controller.signal,
      onProgress: (progress) => {
        if (controller.signal.aborted) return;
        setState({ status: "loading", progress });
      },
    })
      .then((result) => {
        if (controller.signal.aborted) return;
        setState({ status: "ready", index: result.index });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted || isAbortError(error)) return;
        logger.warn("loadEpisodeDiagnostics failed; hiding episode diagnostics", error);
        setState({ status: "unavailable" });
      });
    return () => controller.abort();
  }, [datasetUuid, enabled]);

  return state;
}
